<template>
  <div>
    <div class="section-header">
      <h1 class="section-header__title">Hotels</h1>
      <p class="section-header__subtitle">Curated hotels with exclusive member perks</p>
    </div>

    <!-- Featured Hotels -->
    <div v-if="featuredHotels.length && !hasActiveSearchOrFilter" class="featured-hotels-section">
      <h2 class="section-heading">Featured Hotels</h2>
      <div class="section-grid">
        <PartnerCard v-for="h in featuredHotels" :key="h.id" :partner="h" />
      </div>
    </div>

    <!-- Search & Filter -->
    <div class="hotels-search-section">
      <h2 class="section-heading">All Hotels</h2>

      <div class="search-bar">
        <div class="search-bar__input-wrap">
          <input
            type="text"
            v-model="searchQuery"
            @input="debounceSearch"
            placeholder="Search hotels..."
            class="search-bar__input"
          />
          <svg class="search-bar__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>
          </svg>
        </div>

        <div class="search-bar__filters">
          <!-- Star rating filter -->
          <div class="filter-group">
            <button :class="['filter-pill', { active: !filterStars }]" @click="setStars(null)">All Stars</button>
            <button :class="['filter-pill', { active: filterStars === '5' }]" @click="setStars('5')">5 &#9733;</button>
            <button :class="['filter-pill', { active: filterStars === '4' }]" @click="setStars('4')">4 &#9733;</button>
            <button :class="['filter-pill', { active: filterStars === '3' }]" @click="setStars('3')">3 &#9733;</button>
          </div>

          <!-- Perks filter -->
          <div class="filter-group">
            <button :class="['filter-pill', { active: filterPerks }]" @click="togglePerks">
              {{ filterPerks ? '&#10003; ' : '' }}Member Perks
            </button>
            <button :class="['filter-pill', { active: filterFeatured }]" @click="toggleFeatured">
              {{ filterFeatured ? '&#10003; ' : '' }}Featured
            </button>
          </div>

          <!-- Sort -->
          <select v-model="sortBy" @change="fetchHotels(1)" class="filter-select">
            <option value="popular">Most Popular</option>
            <option value="fora_rating">Highest Rated</option>
            <option value="stars">Star Rating</option>
            <option value="newest">Newest</option>
            <option value="title">A - Z</option>
          </select>
        </div>
      </div>

      <!-- Results Count -->
      <div class="search-results-info" v-if="totalResults > 0">
        <span class="search-results-info__count">{{ totalResults.toLocaleString() }} hotels</span>
        <span v-if="searchQuery" class="search-results-info__query">for "{{ searchQuery }}"</span>
      </div>

      <!-- Loading -->
      <div v-if="loading && hotels.length === 0" class="section-grid">
        <div v-for="n in 8" :key="'sk-' + n" class="section-card section-card--skeleton">
          <div class="section-card__image"></div>
          <div class="section-card__content">
            <div class="skeleton-line skeleton-line--title"></div>
            <div class="skeleton-line skeleton-line--subtitle"></div>
          </div>
        </div>
      </div>

      <!-- No Results -->
      <div v-else-if="!loading && hotels.length === 0" class="search-empty">
        <p>No hotels found.</p>
        <button @click="clearFilters" class="btn-load-more">Clear Filters</button>
      </div>

      <!-- Results Grid -->
      <div v-else class="section-grid">
        <PartnerCard v-for="p in hotels" :key="p.id" :partner="p" />
      </div>

      <!-- Pagination -->
      <div v-if="lastPage > 1" class="pagination">
        <button class="pagination__btn" :disabled="currentPage <= 1" @click="fetchHotels(currentPage - 1)">&laquo; Prev</button>
        <template v-for="page in visiblePages">
          <span v-if="page === '...'" :key="'dot-' + page" class="pagination__dots">...</span>
          <button v-else :key="page" :class="['pagination__btn', { 'pagination__btn--active': page === currentPage }]" @click="fetchHotels(page)">{{ page }}</button>
        </template>
        <button class="pagination__btn" :disabled="currentPage >= lastPage" @click="fetchHotels(currentPage + 1)">Next &raquo;</button>
      </div>
    </div>
  </div>
</template>

<script>
import PartnerCard from '../Shared/PartnerCard.vue'

export default {
  components: { PartnerCard },
  props: {
    featuredHotels: { type: Array, default: () => [] },
    isGuest: { type: Boolean, default: false },
    section: { type: String, default: 'hotels' }
  },
  data() {
    return {
      hotels: [],
      loading: false,
      searchQuery: '',
      filterStars: null,
      filterPerks: false,
      filterFeatured: false,
      sortBy: 'popular',
      currentPage: 1,
      lastPage: 1,
      totalResults: 0,
      searchTimeout: null,
      initialized: false,
    }
  },
  computed: {
    hasActiveSearchOrFilter() {
      return Boolean(
        this.searchQuery ||
        this.filterStars ||
        this.filterPerks ||
        this.filterFeatured
      )
    },
    visiblePages() {
      const pages = []
      const total = this.lastPage
      const current = this.currentPage
      if (total <= 7) { for (let i = 1; i <= total; i++) pages.push(i); return pages }
      pages.push(1)
      if (current > 3) pages.push('...')
      for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
      if (current < total - 2) pages.push('...')
      pages.push(total)
      return pages
    }
  },
  created() {
    this.readUrlParams()
  },
  mounted() {
    this.fetchHotels(this.currentPage)
    window.addEventListener('popstate', this.handlePopState)
  },
  beforeDestroy() {
    window.removeEventListener('popstate', this.handlePopState)
  },
  methods: {
    readUrlParams() {
      const p = new URLSearchParams(window.location.search)
      this.searchQuery = p.get('q') || ''
      this.filterStars = p.get('stars') || null
      this.filterPerks = p.get('perks') === '1'
      this.filterFeatured = p.get('featured') === '1'
      this.sortBy = p.get('sort') || 'popular'
      const pageNum = parseInt(p.get('page'), 10)
      this.currentPage = pageNum > 0 ? pageNum : 1
    },
    syncUrl() {
      const params = new URLSearchParams()
      if (this.searchQuery) params.set('q', this.searchQuery)
      if (this.filterStars) params.set('stars', this.filterStars)
      if (this.filterPerks) params.set('perks', '1')
      if (this.filterFeatured) params.set('featured', '1')
      if (this.sortBy && this.sortBy !== 'popular') params.set('sort', this.sortBy)
      if (this.currentPage > 1) params.set('page', String(this.currentPage))
      const qs = params.toString()
      const newUrl = window.location.pathname + (qs ? '?' + qs : '')
      window.history.replaceState({}, '', newUrl)
    },
    handlePopState() {
      this.readUrlParams()
      this.fetchHotels(this.currentPage)
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => this.fetchHotels(1), 400)
    },
    setStars(val) {
      this.filterStars = val
      this.fetchHotels(1)
    },
    togglePerks() {
      this.filterPerks = !this.filterPerks
      this.fetchHotels(1)
    },
    toggleFeatured() {
      this.filterFeatured = !this.filterFeatured
      this.fetchHotels(1)
    },
    clearFilters() {
      this.searchQuery = ''
      this.filterStars = null
      this.filterPerks = false
      this.filterFeatured = false
      this.sortBy = 'popular'
      this.fetchHotels(1)
    },
    async fetchHotels(page) {
      this.loading = true
      this.currentPage = page

      const params = new URLSearchParams({
        page,
        per_page: 20,
        section: 'hotels',
        sort: this.sortBy,
      })

      if (this.searchQuery) params.set('q', this.searchQuery)
      if (this.filterStars) params.set('hotel_class', this.filterStars)
      if (this.filterPerks) params.set('has_perks', '1')
      if (this.filterFeatured) params.set('featured_only', '1')

      try {
        const res = await fetch(`/api/partners?${params}`)
        const data = await res.json()
        this.hotels = data.data || []
        this.currentPage = data.current_page
        this.lastPage = data.last_page
        this.totalResults = data.total
      } catch (e) {
        console.error('Failed to fetch hotels', e)
      }

      this.loading = false
      this.syncUrl()
      if (this.initialized) window.scrollTo({ top: 0, behavior: 'smooth' })
      this.initialized = true
    }
  }
}
</script>
